import { MessageSquarePlus, Plus, Trash2, Sparkles } from "lucide-react";
import { extractPlaceholders } from "../lib/darQuickPhrases";

/**
 * ChipListEditor — reusable editor for the IR / MR chip catalogues and
 * incident-type catalogue. Used by both the agency-level settings page
 * (ClientPortalSettings) and the per-client override page
 * (AdminClientPortalConfig).
 *
 * Props are intentionally simple: pass in `values` + `onChange(nextArray)`.
 * The editor takes care of add / remove / edit internally.
 */
export default function ChipListEditor({
  label,
  hint,
  values,
  onChange,
  testIdPrefix,
  rows = 1,
  placeholder = "",
  showSlots = false,
  cap = 12,
  disabled = false,
  headerRight = null,
  icon: Icon = MessageSquarePlus,
}) {
  const list = Array.isArray(values) ? values : [];
  const setChip = (idx, val) => {
    if (disabled) return;
    const next = [...list];
    next[idx] = val;
    onChange(next);
  };
  const addChip = () => {
    if (disabled) return;
    onChange([...list, ""]);
  };
  const removeChip = (idx) => {
    if (disabled) return;
    onChange(list.filter((_, i) => i !== idx));
  };

  return (
    <div
      className={`border border-[#27272A] bg-[#0A0A0A] ${disabled ? "opacity-60" : ""}`}
      data-testid={`${testIdPrefix}-section`}
    >
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        <Icon className="size-4 text-zinc-400" />
        <div className="label-mute">{label}</div>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          {list.length} / {cap}
        </span>
        {headerRight}
      </div>
      {hint && (
        <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
          {hint}
          {showSlots && (
            <div className="mt-2 flex items-start gap-1.5 text-[#0A84FF]">
              <Sparkles className="size-3 mt-0.5 flex-shrink-0" />
              <span>
                <span className="font-mono">{"{slot_name}"}</span> — placeholders
                prompt for fill-in on the mobile app.
              </span>
            </div>
          )}
        </div>
      )}
      <div className="divide-y divide-[#1c1c1e]">
        {list.map((val, idx) => {
          const slots = showSlots ? extractPlaceholders(val || "") : [];
          return (
            <div key={idx} className="px-5 py-3 flex items-start gap-3">
              <div className="label-mute mt-3 w-8 flex-shrink-0 font-mono">
                #{idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <textarea
                  rows={rows}
                  value={val}
                  disabled={disabled}
                  onChange={(e) => setChip(idx, e.target.value)}
                  data-testid={`${testIdPrefix}-${idx}`}
                  placeholder={placeholder}
                  className="w-full bg-[#121212] border border-[#27272A] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#FF3B30] disabled:cursor-not-allowed"
                />
                {slots.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                    <span className="text-[9px] uppercase tracking-widest text-[#0A84FF] font-bold">
                      <Sparkles className="size-2.5 inline mr-1" strokeWidth={2.5} />
                      Fill-ins:
                    </span>
                    {slots.map((s) => (
                      <span
                        key={s}
                        className="px-1.5 py-0.5 text-[10px] font-mono text-[#0A84FF] bg-[#0A84FF]/10 border border-[#0A84FF]/30"
                      >
                        {"{" + s + "}"}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeChip(idx)}
                disabled={disabled}
                title="Remove"
                data-testid={`${testIdPrefix}-remove-${idx}`}
                className="p-2 mt-1 text-zinc-500 hover:text-[#FF3B30] transition-colors disabled:cursor-not-allowed disabled:hover:text-zinc-500"
              >
                <Trash2 className="size-3.5" />
              </button>
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="px-5 py-6 text-center text-xs text-zinc-500 italic">
            No entries yet. Click <span className="text-zinc-300">Add</span> to create one.
          </div>
        )}
      </div>
      {list.length < cap && !disabled && (
        <div className="px-5 py-3 border-t border-[#1c1c1e]">
          <button
            type="button"
            onClick={addChip}
            data-testid={`${testIdPrefix}-add`}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] transition-colors"
          >
            <Plus className="size-3.5" /> Add
          </button>
        </div>
      )}
    </div>
  );
}
